// Ask AI on Admin: "which customers have no routing rule?", "who looks after Belk?", "which Deposco
// partners are shown as Amazon?", "which divisions are abbreviated?". The page registers itself with
// the template's chat widget as Shipping does (`axai-shipping.ts`). Its tools read the data the tabs
// already read (`/api/routing/admin/*`, the tabs' own queries: no route of their own) and set the
// open tab's own search box, filter buttons or view, with Undo. Read only: the bar never adds,
// renames, assigns or changes a window. Import has no bar.

import { createContext, useContext, useEffect, useRef } from "react";
import {
  ADMIN_GUIDANCE,
  ADMIN_TAB_NAMES,
  APP_PAGES,
  adminViewRoute,
  adminViewKey,
  applyAdminView,
  queryAssignments,
  queryCustomerNames,
  queryDivisions,
  queryRoutingRules,
} from "@app/shared";
import type {
  AdminView,
  AssignmentsPayload,
  CustomerNamesPayload,
  DivisionsPayload,
  RoutingRulesPayload,
} from "@app/shared";
import type { AxaiApp, Tool } from "./axai-prompt";
import type { AdminBarTab } from "./ask-ai-bar";

/** What the open tab gives Ask AI: its filters now, how to set them, and (assignments) its people. */
export interface AdminAskTab {
  view: AdminView;
  apply: (next: AdminView) => void;
  /** The rows the tab would show with a view, as it counts them. */
  shownFor: (view: AdminView) => number;
  people?: readonly { email: string; name: string }[];
}

/** The tab registers here (`useAdminAskTab`); the page reads it at each question. */
export interface AdminAskSlot {
  tab: { current: AdminAskTab | null };
  /** The tab's view changed: the bar's Undo and answer follow it. */
  changed: (view: AdminView) => void;
}
export const AdminAskContext = createContext<AdminAskSlot | null>(null);

/** A tab's filters, given to Ask AI while the tab is open. Outside the Admin page it does nothing. */
export function useAdminAskTab(entry: AdminAskTab): void {
  const slot = useContext(AdminAskContext);
  if (slot) slot.tab.current = entry;
  const key = adminViewKey(entry.view);
  useEffect(() => {
    slot?.changed(entry.view);
    // The key says the view: a new object for the same filters changes nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot, key]);
  useEffect(
    () => () => {
      if (slot?.tab.current === entry) slot.tab.current = null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slot],
  );
}

/** The tabs' data, read as each tab reads it (their own query keys, cached). */
export interface AdminReads {
  rules: () => Promise<RoutingRulesPayload>;
  customers: () => Promise<CustomerNamesPayload>;
  assignments: () => Promise<AssignmentsPayload>;
  divisions: () => Promise<DivisionsPayload>;
}

export interface AdminAskPage {
  tab: AdminBarTab;
  slot: AdminAskSlot;
  reads: AdminReads;
  /** Sets the open tab's view, one Undo step back to `before` (default: the tab's view now). */
  apply: (next: AdminView, unapplied: string[], before?: AdminView) => void;
  /** Switches to a tab and resolves once it has registered its filters. */
  open: (tab: AdminBarTab) => Promise<AdminAskTab>;
  /** The tabs a question's filters are on, offered under the bar as links. */
  offer: (tabs: AdminBarTab[]) => void;
}

const SOURCE = { type: "string", enum: ["all", "A2000", "Deposco"] };
const LIMIT = { type: "integer", description: "Rows to list, 0–25 (default 10). Counts cover every match." };

export const ADMIN_TOOLS: Tool[] = [
  {
    name: "query_routing_rules",
    description:
      "Admin → Routing rules: customers (a shown name; customers sharing one are one row) by their window in use " +
      "— none, from A2000, set in the app, turned off — with codes, days and open lines. 'Which customers have no " +
      "window and open lines' = state none (open lines only is the default).",
    input_schema: {
      type: "object",
      properties: {
        state: { type: "string", enum: ["none", "a2000", "app", "off"] },
        customer: { type: "string", description: "A shown name, original name or code, or a part of one." },
        source: SOURCE,
        group: { type: "boolean", description: "true: groups only (2+ codes); false: single customers only." },
        with_open_lines: { type: "boolean", description: "Default true, as the tab." },
        open_bdays: { type: "integer", description: "Window opens: business days before First ship." },
        due_bdays: { type: "integer", description: "Route by: business days before Cancel." },
        limit: LIMIT,
      },
    },
  },
  {
    name: "query_assignments",
    description:
      "Admin → Customer assignments: who owns and backs up a customer (customer), a person's customers " +
      "(person), customers with open lines and no Owner (no_owner), or labels needing confirming " +
      "(needs_confirming: no linked code yet). One of them per call.",
    input_schema: {
      type: "object",
      properties: {
        customer: { type: "string", description: "A shown name, original name or code." },
        person: { type: "string", description: "A person's name (or part) or email." },
        no_owner: { type: "boolean" },
        needs_confirming: { type: "boolean" },
        limit: LIMIT,
      },
    },
  },
  {
    name: "query_customer_names",
    description:
      "Admin → Customers: the name every page shows for a customer, which codes share one (shared: true), " +
      "customers named in Admin or showing the default, by source.",
    input_schema: {
      type: "object",
      properties: {
        search: { type: "string", description: "A shown name, original name or code, or a part." },
        source: SOURCE,
        named: { type: "string", enum: ["named", "default"] },
        shared: { type: "boolean", description: "List shown names two or more customers share, with their codes." },
        with_open_lines: { type: "boolean" },
        limit: LIMIT,
      },
    },
  },
  {
    name: "query_divisions",
    description:
      "Admin → Divisions: each division's abbreviation, its default, whether it was set in Admin, open lines.",
    input_schema: {
      type: "object",
      properties: {
        search: { type: "string" },
        source: SOURCE,
        abbreviated: { type: "boolean", description: "true: set in Admin; false: showing the default." },
        limit: LIMIT,
      },
    },
  },
  {
    name: "apply_view",
    description:
      "Set the open tab's own search box, filter buttons or view in one call; only what is named changes. " +
      "When every filter named is another tab's, that tab opens and they are set there (opened). " +
      "Routing rules: search, source, state (all, none, a2000, app, off), with_open_lines. Customers: search, " +
      "source, named (all, named, default), with_open_lines. Customer assignments: view (needs, assignments, " +
      "no-owner), search, person. Divisions: search, source. reset: the tab's starting filters. The user can " +
      "undo it.",
    input_schema: {
      type: "object",
      properties: {
        search: { type: "string" },
        source: SOURCE,
        state: { type: "string", enum: ["all", "none", "a2000", "app", "off"] },
        with_open_lines: { type: "boolean" },
        named: { type: "string", enum: ["all", "named", "default"] },
        view: { type: "string", enum: ["needs", "assignments", "no-owner"] },
        person: { type: "string" },
        reset: { type: "boolean" },
      },
    },
  },
];

const TAB_NAMES = ADMIN_TAB_NAMES;

function systemPrompt(p: AdminAskPage) {
  return [
    {
      type: "text" as const,
      text: [
        `You are Ask AI in Production & Routing, the AXNY warehouse's app. The user is a manager on Admin → ` +
          `${TAB_NAMES[p.tab]}.`,
        APP_PAGES,
        "## Definitions",
        `- ${ADMIN_GUIDANCE}`,
        "## Rules",
        "- Take every number and name from a tool answer. Name the tab and say each row as the tool gives it.",
        "- You only read and set the open tab's own filters: you cannot add, rename, assign, link, retire or " +
          "change a window, and must not offer to. The manager does it on the tab.",
        "- Answer briefly.",
      ].join("\n"),
    },
  ];
}

/** Each tab's own read: a tab opened for a question has its rows when it registers. */
const READ_OF: Record<AdminBarTab, (r: AdminReads) => () => Promise<unknown>> = {
  "routing-rules": (r) => r.rules,
  assignments: (r) => r.assignments,
  customers: (r) => r.customers,
  divisions: (r) => r.divisions,
};

export function useAdminAskAi(page: AdminAskPage): void {
  const ref = useRef(page);
  ref.current = page;
  useEffect(() => {
    const app: AxaiApp = {
      systemPrompt: () => systemPrompt(ref.current),
      handleToolCall: async (name, input, appendStatus, isStale) => {
        const p = ref.current;
        const q = (input ?? {}) as Record<string, unknown>;
        try {
          switch (name) {
            case "query_routing_rules":
              appendStatus("Reading Routing rules…");
              return JSON.stringify(queryRoutingRules((await p.reads.rules()).rows, q));
            case "query_assignments":
              appendStatus("Reading Customer assignments…");
              return JSON.stringify(queryAssignments(await p.reads.assignments(), q));
            case "query_customer_names":
              appendStatus("Reading Customers…");
              return JSON.stringify(queryCustomerNames((await p.reads.customers()).customers, q));
            case "query_divisions":
              appendStatus("Reading Divisions…");
              return JSON.stringify(queryDivisions((await p.reads.divisions()).divisions, q));
            case "apply_view": {
              if (isStale()) return "Not applied: the conversation moved on.";
              const here = p.slot.tab.current;
              if (!here) return JSON.stringify({ applied: false, why: "This tab has no filters Ask AI can set." });
              const route = adminViewRoute(here.view.tab, q);
              // Every filter named is another tab's: open it (its data read first) and set them there.
              if (route.to === "open") {
                const before = here.view;
                await READ_OF[route.tab](p.reads)();
                const there = await p.open(route.tab);
                const next = applyAdminView(there.view, q, there.people);
                p.apply(next.view, next.unapplied, before);
                const shown = there.shownFor(next.view);
                return JSON.stringify({
                  opened: `Admin → ${TAB_NAMES[route.tab]}`,
                  rows_shown: shown,
                  not_applied: next.unapplied,
                  say: `Opened ${TAB_NAMES[route.tab]}: ${shown} … (what the filters keep).`,
                  undo: "The user can undo it with the Undo button under the Ask AI bar: back to the tab they were on.",
                });
              }
              const next = applyAdminView(here.view, q, here.people);
              p.apply(next.view, next.unapplied);
              // Filters on two other tabs: what this one has is set; the others are offered as links.
              if (route.to === "elsewhere" && route.tabs.length) p.offer(route.tabs);
              return JSON.stringify({
                tab: `Admin → ${TAB_NAMES[here.view.tab]}`,
                rows_shown: here.shownFor(next.view),
                not_applied: next.unapplied,
                ...(route.to === "elsewhere"
                  ? { open_tab: route.tabs.map((t) => TAB_NAMES[t]), links: "Links to them are under the bar." }
                  : {}),
                undo: "The user can undo it with the Undo button under the Ask AI bar.",
              });
            }
            default:
              return JSON.stringify({ error: `Unknown tool ${name}` });
          }
        } catch (e) {
          return JSON.stringify({ error: e instanceof Error ? e.message : String(e) });
        }
      },
    };
    window.__AXAI_APP__ = app;
    window.AXAI_TOOLS = ADMIN_TOOLS;
    document.body.classList.add("axai-ready");
    return () => {
      if (window.__AXAI_APP__ === app) delete window.__AXAI_APP__;
      window.AXAI_TOOLS = [];
      document.body.classList.remove("axai-ready");
    };
  }, []);
}
